<template>
  <div class="pwd-manager">
    <!-- 顶部导航栏 -->
    <AppHeader :darkMode="darkMode" @toggle-theme="toggleTheme" />

    <!-- 主内容区 -->
    <main class="container mx-auto px-4 pt-24 pb-16">
      <!-- 控制面板 -->
      <div class="bg-white rounded-lg shadow-md p-4 mb-6">
        <div class="flex flex-col md:flex-row justify-between items-center gap-4">
          <div class="w-full md:w-auto">
            <h2 class="text-xl font-semibold mb-2">账号管理</h2>
            <p class="text-gray-600">安全管理您的所有账号和密码</p>
          </div>
          <div class="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div class="relative w-full sm:w-64">
              <input 
                type="text" 
                v-model="searchTerm"
                placeholder="搜索账号..." 
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 pl-10"
              >
              <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
            <button @click="openAddModal" class="bg-secondary hover:bg-secondary/90 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-custom w-full sm:w-auto">
              <i class="fa fa-plus"></i>
              <span>添加新账号</span>
            </button>
            <button @click="exportToTxt" class="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-custom w-full sm:w-auto">
              <i class="fa fa-download"></i>
              <span>导出</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 统计卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-primary">
          <div class="flex justify-between items-start">
            <div>
              <p class="text-gray-600 text-sm">总账号数</p>
              <h3 class="text-2xl font-bold mt-1">{{ totalCount }}</h3>
            </div>
            <div class="bg-primary/10 p-3 rounded-full">
              <i class="fa fa-database text-primary text-xl"></i>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-secondary">
          <div class="flex justify-between items-start">
            <div>
              <p class="text-gray-600 text-sm">最近添加</p>
              <h3 class="text-2xl font-bold mt-1">{{ recentCount }}</h3>
            </div>
            <div class="bg-secondary/10 p-3 rounded-full">
              <i class="fa fa-clock-o text-secondary text-xl"></i>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-danger">
          <div class="flex justify-between items-start">
            <div>
              <p class="text-gray-600 text-sm">需要更新</p>
              <h3 class="text-2xl font-bold mt-1">{{ expiredCount }}</h3>
            </div>
            <div class="bg-danger/10 p-3 rounded-full">
              <i class="fa fa-exclamation-triangle text-danger text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- 账号列表 -->
      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">平台</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">平台地址</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">账号</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">密码</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">添加日期</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-if="filteredPasswords.length === 0" class="text-center">
                <td colspan="7" class="px-6 py-12 text-gray-500">
                  <div class="flex flex-col items-center">
                    <i class="fa fa-folder-open-o text-4xl mb-3 text-gray-300"></i>
                    <p>暂无账号数据，请添加新账号</p>
                  </div>
                </td>
              </tr>
              <tr v-for="item in filteredPasswords" :key="item.id" class="hover:bg-gray-50 transition-custom">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                  {{ item.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="bg-primary/10 p-2 rounded mr-3">
                      <i class="fa fa-globe text-primary"></i>
                    </div>
                    <div>
                      <div class="font-medium">{{ item.platform }}</div>
                      <div v-if="item.notes" class="text-sm text-gray-500">{{ item.notes }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <a v-if="item.url" :href="item.url" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">{{ item.url }}</a>
                  <span v-else class="text-gray-400">-</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">{{ item.username }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <span class="password-display">{{ item.showPassword ? item.password : '••••••••' }}</span>
                    <button @click="togglePasswordVisibility(item.id)" class="show-password ml-2 text-gray-500 hover:text-primary">
                      <i :class="item.showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                    </button>
                    <button @click="copyPasswordToClipboard(item.password)" class="copy-password ml-2 text-gray-500 hover:text-secondary">
                      <i class="fa fa-copy"></i>
                    </button>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(item.createdAt) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex space-x-2">
                    <button @click="openEditModal(item)" class="edit-btn text-primary hover:text-primary/80">
                      <i class="fa fa-pencil"></i> 编辑
                    </button>
                    <button @click="openDeleteModal(item)" class="delete-btn text-danger hover:text-danger/80">
                      <i class="fa fa-trash"></i> 删除
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>

    <AppFooter />

    <!-- 添加/编辑账号模态框 -->
    <div v-if="showPasswordModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 opacity-100 transition-custom">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 transform scale-100 transition-custom">
        <div class="p-5 border-b">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-bold">{{ currentEditId ? '编辑账号' : '添加新账号' }}</h3>
            <button @click="closePasswordModal" class="text-gray-500 hover:text-gray-700">
              <i class="fa fa-times text-xl"></i>
            </button>
          </div>
        </div>
        <div class="p-5">
          <form @submit.prevent="savePassword">
            <input type="hidden" v-model="currentEditId">
            <div class="mb-4">
              <label for="platform" class="block text-sm font-medium text-gray-700 mb-1">平台名称</label>
              <input type="text" id="platform" v-model="form.platform" required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50">
            </div>
            <div class="mb-4">
              <label for="url" class="block text-sm font-medium text-gray-700 mb-1">平台地址 (可选)</label>
              <input type="text" id="url" v-model="form.url" placeholder="https://example.com"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50">
            </div>
            <div class="mb-4">
              <label for="username" class="block text-sm font-medium text-gray-700 mb-1">账号/用户名</label>
              <input type="text" id="username" v-model="form.username" required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50">
            </div>
            <div class="mb-4">
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">密码</label>
              <div class="relative">
                <input :type="showFormPassword ? 'text' : 'password'" id="password" v-model="form.password" required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 pr-20">
                <div class="absolute right-1 top-1/2 -translate-y-1/2 flex">
                  <button type="button" @click="toggleFormPassword" class="text-gray-500 hover:text-primary mr-1">
                    <i :class="showFormPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                  </button>
                  <button type="button" @click="generateRandomPassword" class="text-gray-500 hover:text-primary">
                    <i class="fa fa-magic"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="mb-4">
              <label for="notes" class="block text-sm font-medium text-gray-700 mb-1">备注 (可选)</label>
              <textarea id="notes" v-model="form.notes" rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"></textarea>
            </div>
          </form>
        </div>
        <div class="p-5 border-t flex justify-end gap-3">
          <button @click="closePasswordModal" class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-custom">取消</button>
          <button @click="savePassword" class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-custom">保存</button>
        </div>
      </div>
    </div>

    <!-- 确认删除模态框 -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 opacity-100 transition-custom">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 transform scale-100 transition-custom">
        <div class="p-5 border-b">
          <h3 class="text-xl font-bold text-danger">确认删除</h3>
        </div>
        <div class="p-5">
          <p>您确定要删除这个账号信息吗？此操作无法撤销。</p>
          <p class="text-gray-500 text-sm mt-2">平台: <span class="font-medium">{{ deleteItem && deleteItem.platform }}</span></p>
        </div>
        <div class="p-5 border-t flex justify-end gap-3">
          <button @click="closeDeleteModal" class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-custom">取消</button>
          <button @click="confirmDelete" class="px-4 py-2 bg-danger text-white rounded-md hover:bg-danger/90 transition-custom">删除</button>
        </div>
      </div>
    </div>

    <!-- 通知提示 -->
    <div v-if="notification.show" :class="[
      'fixed bottom-5 right-5 p-4 rounded-lg shadow-lg transform translate-y-0 opacity-100 transition-custom max-w-xs',
      notification.type === 'success' ? 'bg-green-50 border border-green-200' :
      notification.type === 'error' ? 'bg-red-50 border border-red-200' :
      'bg-blue-50 border border-blue-200'
    ]">
      <div class="flex items-center gap-3">
        <div :class="[
          'text-xl',
          notification.type === 'success' ? 'fa fa-check-circle text-green-500' :
          notification.type === 'error' ? 'fa fa-exclamation-circle text-red-500' :
          'fa fa-info-circle text-blue-500'
        ]"></div>
        <div>
          <h4 class="font-semibold">{{ notification.title }}</h4>
          <p class="text-sm text-gray-600">{{ notification.message }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import CryptoJS from 'crypto-js'
import AppHeader from './header/AppHeader.vue'
import AppFooter from './footer/AppFooter.vue'

// 导出/加载密码使用的固定 AES 密钥（来自环境配置文件 .env.local）
const EXPORT_PASSPHRASE = process.env.VUE_APP_AES_KEY

// 密码数据接口
const passwords = ref(loadPasswordsFromStorage())
const darkMode = ref(loadDarkModeFromStorage())

// 加载本地存储密码数据的函数
function loadPasswordsFromStorage() {
  try {
    const stored = localStorage.getItem('passwords')
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

//加载主题函数
function loadDarkModeFromStorage() {
  try {
    return localStorage.getItem('darkMode') === 'true'
  } catch {
    return false
  }
}
const searchTerm = ref('')
const showPasswordModal = ref(false)
const showDeleteModal = ref(false)
const currentEditId = ref(null)
const deleteItem = ref(null)
const showFormPassword = ref(false)

// 表单数据
const form = ref({
  platform: '',
  url: '',
  username: '',
  password: '',
  notes: ''
})

// 通知数据
const notification = ref({
  show: false,
  title: '',
  message: '',
  type: 'info'
})

// 计算属性
const filteredPasswords = computed(() => {
  if (!searchTerm.value.trim()) return passwords.value
  
  const term = searchTerm.value.toLowerCase()
  return passwords.value.filter(item => 
    item.platform.toLowerCase().includes(term) || 
    item.username.toLowerCase().includes(term) ||
    (item.notes && item.notes.toLowerCase().includes(term))
  )
})

const totalCount = computed(() => passwords.value.length)

const recentCount = computed(() => {
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  return passwords.value.filter(item => new Date(item.createdAt) >= thirtyDaysAgo).length
})

const expiredCount = computed(() => {
  const ninetyDaysAgo = new Date()
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)
  return passwords.value.filter(item => new Date(item.createdAt) <= ninetyDaysAgo).length
})

// 生命周期
onMounted(async () => {
  // 应用主题
  if (darkMode.value) {
    document.body.classList.add('bg-gray-900', 'text-white')
    document.body.classList.remove('bg-gray-100', 'text-dark')
  }
  // 启动时从 Cloudflare D1 加载数据，如果失败则使用 localStorage
  try {
    const res = await fetch('/api/passwords');
    if (res.ok) {
      const json = await res.json();
      if (json.success && json.data && json.data.length > 0) {
        // 数据从后端加载成功
        passwords.value = json.data.map(item => ({
          ...item,
          password: item.password, // D1 存的密文，需在 UI 显示时解密
          showPassword: false
        }));
        // 解密密码（假设 D1 存的是密文）
        decryptAllPasswords();
        saveToLocalStorage(); // 同步到本地缓存
        showNotification('提示', '已从云端加载数据', 'info');
      }
    } else {
      throw new Error('API request failed');
    }
  } catch (err) {
    console.warn('无法连接云端 API，使用本地缓存数据：', err);
    showNotification('提示', '离线模式：使用本地缓存数据', 'info');
  }
})

// 解密密码列表
const decryptAllPasswords = () => {
  passwords.value.forEach(item => {
    try {
      // D1 存储的是 AES 加密的密文
      const plain = CryptoJS.AES.decrypt(item.password, EXPORT_PASSPHRASE).toString(CryptoJS.enc.Utf8);
      if (plain) {
        item.password = plain;
      }
    } catch (e) {
      console.warn('解密失败:', item.id);
    }
  });
}

// 方法
const openAddModal = () => {
  form.value = { platform: '', url: '', username: '', password: '', notes: '' }
  currentEditId.value = null
  showFormPassword.value = false
  showPasswordModal.value = true
}

const openEditModal = (item) => {
  form.value = {
    platform: item.platform,
    url: item.url || '',
    username: item.username,
    password: item.password,
    notes: item.notes || ''
  }
  currentEditId.value = item.id
  showFormPassword.value = false
  showPasswordModal.value = true
}

const openDeleteModal = (item) => {
  deleteItem.value = item
  showDeleteModal.value = true
}

const closePasswordModal = () => {
  showPasswordModal.value = false
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  deleteItem.value = null
}

const savePassword = async () => {
  const { platform, url, username, password, notes } = form.value

  if (!platform.trim() || !username.trim() || !password.trim()) {
    showNotification('错误', '请填写所有必填字段', 'error')
    return
  }

  if (currentEditId.value) {
    // 更新现有账号
    const index = passwords.value.findIndex(p => p.id === currentEditId.value)
    if (index !== -1) {
      passwords.value[index] = {
        ...passwords.value[index],
        platform: platform.trim(),
        url: url.trim(),
        username: username.trim(),
        password: password.trim(),
        notes: notes.trim(),
        updatedAt: new Date().toISOString()
      }
      showNotification('成功', '账号信息已更新', 'success')
    }
    // 同步到云端
    saveToLocalStorage(); // 先保存本地
    const itemToUpdate = passwords.value[index];
    try {
      const encryptedPwd = CryptoJS.AES.encrypt(itemToUpdate.password, EXPORT_PASSPHRASE).toString();
      await fetch(`/api/passwords/${itemToUpdate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...itemToUpdate, password: encryptedPwd })
      });
    } catch (e) {
      console.error('同步更新失败:', e);
      showNotification('警告', '本地已更新，但云端同步失败', 'error');
    }
  } else {
    // 添加新账号
    const newItem = {
      id: generateId(),
      platform: platform.trim(),
      url: url.trim(),
      username: username.trim(),
      password: password.trim(),
      notes: notes.trim(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      showPassword: false
    }
    passwords.value.unshift(newItem)
    showNotification('成功', '新账号已添加', 'success')
    
    // 同步到云端
    saveToLocalStorage(); // 先保存本地
    try {
      const encryptedPwd = CryptoJS.AES.encrypt(newItem.password, EXPORT_PASSPHRASE).toString();
      const res = await fetch('/api/passwords', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newItem, password: encryptedPwd })
      });
      // 用服务端雪花算法生成的 id 更新本地
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.id) {
          newItem.id = json.id;
          saveToLocalStorage();
        }
      }
    } catch (e) {
      console.error('同步新增失败:', e);
      showNotification('警告', '本地已添加，但云端同步失败', 'error');
    }
  }
  
  closePasswordModal()
}

const confirmDelete = async () => {
  if (deleteItem.value) {
    const idToDelete = deleteItem.value.id;
    passwords.value = passwords.value.filter(p => p.id !== idToDelete);
    saveToLocalStorage(); // 先保存本地
    closeDeleteModal();
    showNotification('成功', '账号已删除', 'success');
    
    // 同步到云端
    try {
      await fetch(`/api/passwords/${idToDelete}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.error('同步删除失败:', e);
      showNotification('警告', '本地已删除，但云端同步失败', 'error');
    }
  }
}

const togglePasswordVisibility = (id) => {
  const item = passwords.value.find(p => p.id === id)
  if (item) {
    item.showPassword = !item.showPassword
  }
}

const copyPasswordToClipboard = async (password) => {
  try {
    await navigator.clipboard.writeText(password)
    showNotification('成功', '密码已复制到剪贴板', 'success')
  } catch (err) {
    showNotification('错误', '无法复制密码', 'error')
    console.error('复制失败:', err)
  }
}

const toggleFormPassword = () => {
  showFormPassword.value = !showFormPassword.value
}

const generateRandomPassword = () => {
  const length = 12
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-='
  let password = ''
  
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length)
    password += charset[randomIndex]
  }
  
  form.value.password = password
  showNotification('提示', '已生成随机密码', 'info')
}

const toggleTheme = () => {
  darkMode.value = !darkMode.value
  
  if (darkMode.value) {
    document.body.classList.add('bg-gray-900', 'text-white')
    document.body.classList.remove('bg-gray-100', 'text-dark')
  } else {
    document.body.classList.remove('bg-gray-900', 'text-white')
    document.body.classList.add('bg-gray-100', 'text-dark')
  }
  
  localStorage.setItem('darkMode', darkMode.value)
}

const showNotification = (title, message, type = 'info') => {
  notification.value = { show: true, title, message, type }
  
  setTimeout(() => {
    notification.value.show = false
  }, 3000)
}

const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5)
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', { 
    year: 'numeric', 
    month: '2-digit', 
    day: '2-digit' 
  })
}

const saveToLocalStorage = () => {
  localStorage.setItem('passwords', JSON.stringify(passwords.value))
}

// 导出所有账号到 txt 文件，密码使用固定密钥 AES 加密
const exportToTxt = async () => {
  if (passwords.value.length === 0) {
    showNotification('提示', '暂无账号数据可导出', 'info')
    return
  }

  try {
    // 从云端获取最新数据
    const res = await fetch('/api/export');
    if (!res.ok) throw new Error('fetch failed');
    const json = await res.json();
    
    if (!json.success) throw new Error(json.error || 'export failed');

    const lines = [
      '密码本导出文件',
      `导出时间：${new Date().toLocaleString('zh-CN')}`,
      '密码存储方式：明文',
      '========================================',
      ''
    ]

    json.data.forEach((item, index) => {
      // D1 存储的是 AES 密文，导出时解密为明文
      let plainPwd = item.password
      try {
        const decrypted = CryptoJS.AES.decrypt(item.password, EXPORT_PASSPHRASE).toString(CryptoJS.enc.Utf8)
        if (decrypted) plainPwd = decrypted
      } catch (e) {
        // 解密失败则原样输出
      }

      lines.push(`【${index + 1}】平台：${item.title || item.platform}`)
      lines.push(`    账号：${item.username}`)
      lines.push(`    密码：${plainPwd}`)
      if (item.url) lines.push(`    平台地址：${item.url}`)
      if (item.remark || item.notes) lines.push(`    备注：${item.remark || item.notes}`)
      lines.push(`    创建时间：${item.create_time || formatDate(item.createdAt)}`)
      lines.push('')
    })

    const content = lines.join('\n')
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'password.txt'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    showNotification('成功', `已导出 ${json.data.length} 条账号到 password.txt`, 'success')
  } catch (err) {
    console.error('导出失败:', err)
    showNotification('错误', '导出失败，请检查网络连接', 'error')
  }
}


</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');


body {
  font-family: 'Inter', 'system-ui', 'sans-serif';
}

/* 
@layer utilities {
  .content-auto {
    content-visibility: auto;
  }
  .card-shadow {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
  .transition-custom {
    transition: all 0.3s ease;
  }
}
*/

.password-display {
  font-family: monospace;
  letter-spacing: 1px;
}

#app {
  min-height: 100vh;
}
</style>